import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import clsx from 'clsx';
import { Button, InlineMessages, InlineMessagesType, Types } from '@marriott/mi-ui-library';
import { useMutation } from '@apollo/client';
import { ReSubmitRfpResponse, reSubmitRfpMutation } from '@marriott/mi-groups-graphql';
import { createCustomXRequestId } from '../../../../utils';
import { useLocaleStore, useHeaderFieldsStore } from '../../../../store';
import { TextFormField } from '../../../../molecules';
import { RESUBMIT_RFP } from '../../../asp-constants';
import { ResubmitRfpForm } from './ResubmitRfp.types';
import { StyledResubmitRfp, StyledResubmitRfpForm } from './ResubmitRfp.styles';

export const ResubmitRfp = () => {
  const { title, qgIdsLables, submitCta } = RESUBMIT_RFP;

  const { locale } = useLocaleStore();
  const { requestId } = useHeaderFieldsStore();

  const {
    control,
    getValues,
    formState: { errors },
    handleSubmit,
    clearErrors,
  } = useForm<ResubmitRfpForm>();

  const [apiError, setApiError] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [reSubmitRfp, { loading }] = useMutation<ReSubmitRfpResponse>(reSubmitRfpMutation);

  const parseRfpIds = (inputIds: string): string[] => {
    if (!inputIds) return [];
    return inputIds
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  };

  const reSubmitRfpCallback = () => {
    const rfpIds = parseRfpIds(getValues('qgIds'));
    if (rfpIds.length === 0) return;
    if (rfpIds.length > 50) {
      setApiError(true);
      setErrorMessage(RESUBMIT_RFP.maxRfpIdLength);
      window.scrollTo(0, 0);
      return;
    }

    reSubmitRfp({
      variables: {
        input: {
          ids: rfpIds,
        },
      },
      context: {
        headers: {
          'accept-language': locale,
          'x-request-id': createCustomXRequestId('quick_group', 'submission', requestId, locale),
        },
      },
      fetchPolicy: 'no-cache',
      onCompleted: data => {
        const node = data?.sales?.groupSales?.groupsReSubmitRfp?.edges?.[0]?.node;
        const reSubmitDetails = node?.reSubmitDetails ?? [];
        const errors = node?.errors ?? [];
        if (reSubmitDetails.length && reSubmitDetails[0].rfpId && !errors.length) {
          setSuccessMessage(true);
        } else {
          setApiError(true);
          setErrorMessage(RESUBMIT_RFP.invalidErrorMessage);
        }
      },
      onError: () => {
        setApiError(true);
        window.scrollTo(0, 0);
      },
    });
  };

  return (
    <StyledResubmitRfp data-testid="groups-ResubmitRfp" data-component-name="m-groups-ResubmitRfp" className="mt-3">
      <div className="container pb-3 px-0">
        {apiError ? (
          <div className="mb-4">
            <InlineMessages
              type={InlineMessagesType.Error}
              title={errorMessage || RESUBMIT_RFP.apiError}
              severity="1"
            />
          </div>
        ) : null}
        {successMessage ? (
          <div className="mb-4">
            <InlineMessages type={InlineMessagesType.Success} title={RESUBMIT_RFP.successMessage} severity="1" />
          </div>
        ) : null}
        <form onSubmit={handleSubmit(reSubmitRfpCallback)} className="my-7">
          <StyledResubmitRfpForm>
            <div className="t-subtitle-l title">{title}</div>
            <div className="row mt-5">
              <div className="col-12 pb-5">
                <Controller
                  name="qgIds"
                  control={control}
                  rules={{
                    required: qgIdsLables.requiredError,
                    validate: (val: string) => {
                      const ids = parseRfpIds(val);
                      return ids.length > 0 || qgIdsLables.requiredError;
                    },
                  }}
                  render={({ field }) => (
                    <TextFormField
                      {...field}
                      type="text"
                      label={qgIdsLables.label}
                      ariaLabel={qgIdsLables.ariaLabel}
                      className="m-input-field"
                      showErrorMessage={!!errors?.qgIds?.message}
                      errorMessage={errors?.qgIds?.message}
                      testId="qg-ids"
                      onChange={event => {
                        field.onChange(event);
                        clearErrors(field.name);
                      }}
                    />
                  )}
                />
              </div>
            </div>
            <Button
              type={Types.ButtonTypeVariation.Submit}
              className={clsx('m-button-primary m-button-m submit-cta', loading && 'skeleton-loader')}
              ariaLabel={submitCta.ariaLabel}
              isDisabled={loading}
              custom_click_track_value={`${submitCta.location} | ${submitCta.description} | internal`}
            >
              {submitCta.label}
            </Button>
          </StyledResubmitRfpForm>
        </form>
      </div>
    </StyledResubmitRfp>
  );
};
